import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiPlus, FiSearch } from 'react-icons/fi';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';
import { useDatabase } from '../../hooks/useDatabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useSnackbar } from 'notistack';

interface PriorityItemsListProps {
  database: wasm.WrappedDatabase;
  workspaceId: string | undefined;
  /** The owner of this priority list (team or person). */
  owner: { type_: wasm.NodeType; id: string };
  /** Show the add-item form inline. Default true. */
  editable?: boolean;
}

/**
 * Ordered list of priority items (PriorityItem with fractional indexing).
 * Can be embedded in the Priorities page or the Team Dashboard.
 */
const PriorityItemsList: React.FC<PriorityItemsListProps> = ({
  database,
  workspaceId,
  owner,
  editable = true,
}) => {
  const { applyCommand } = useDatabase();
  const { buildUrl } = useWorkspace();
  const { enqueueSnackbar } = useSnackbar();
  const [showAddForm, setShowAddForm] = useState(false);
  const [addQuery, setAddQuery] = useState('');

  const items: any[] = useMemo(() => {
    try {
      const raw = database.priority_items_for_owner(owner.type_, owner.id);
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  }, [database, owner.type_, owner.id]);

  // Searchable entities for adding new items
  const addableEntities = useMemo(() => {
    if (!showAddForm) return [];
    const currentIds = new Set(items.map((i: any) => i.entity_id?.id).filter(Boolean));
    const results: { type_: wasm.NodeType; id: string; label: string }[] = [];

    const wfInstances = database.workflow_instances();
    for (const [id, inst] of Object.entries(wfInstances)) {
      if (!currentIds.has(id)) {
        results.push({ type_: 'WorkflowInstance', id, label: inst.title() || `Workflow ${id.slice(0, 8)}` });
      }
    }
    const tasks = database.tasks();
    for (const [id, task] of Object.entries(tasks)) {
      if (!currentIds.has(id)) {
        results.push({ type_: 'Task', id, label: task.title() });
      }
    }
    const outcomes = database.outcomes();
    for (const [id, outcome] of Object.entries(outcomes)) {
      if (!currentIds.has(id)) {
        results.push({ type_: 'Outcome', id, label: outcome.title() });
      }
    }
    return results;
  }, [database, items, showAddForm]);

  const filteredAddable = useMemo(() => {
    if (!addQuery.trim()) return addableEntities.slice(0, 20);
    const q = addQuery.toLowerCase();
    return addableEntities.filter(e => e.label.toLowerCase().includes(q)).slice(0, 20);
  }, [addableEntities, addQuery]);

  const labelForEntity = (entityId: any): { label: string; type: string; link?: string } => {
    const type = entityId?.type_;
    const id = entityId?.id;
    if (!type || !id) return { label: 'Unknown', type: 'Unknown' };

    if (type === 'WorkflowInstance') {
      const instances = database.workflow_instances();
      const inst = instances[id];
      return {
        label: inst ? inst.title() || `Workflow ${id.slice(0, 8)}` : `Workflow ${id.slice(0, 8)}`,
        type: 'Workflow',
        link: buildUrl({ kind: 'workflow-instance', instanceId: id }),
      };
    }
    if (type === 'Task') {
      const tasks = database.tasks();
      const task = tasks[id];
      return {
        label: task ? task.title() : `Task ${id.slice(0, 8)}`,
        type: 'Task',
        link: buildUrl({ kind: 'task', taskId: id }),
      };
    }
    if (type === 'Outcome') {
      const outcomes = database.outcomes();
      const outcome = outcomes[id];
      return {
        label: outcome ? outcome.title() : `Outcome ${id.slice(0, 8)}`,
        type: 'Outcome',
        link: buildUrl({ kind: 'outcome', outcomeId: id }),
      };
    }
    return { label: `${type} ${id.slice(0, 8)}`, type };
  };

  const handleRemove = async (itemId: string) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await applyCommand({ type: 'RemovePriorityItem', owner, id: itemId } as any);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to remove', { variant: 'error' });
    }
  };

  const handleAdd = async (entity: { type_: wasm.NodeType; id: string }) => {
    try {
      // Append after the last existing item by extending its position string.
      const position = items.length > 0
        ? items[items.length - 1].position + 'z'
        : 'a0';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await applyCommand({
        type: 'AddPriorityItem',
        owner,
        id: uuidv4(),
        entity_id: { type_: entity.type_, id: entity.id },
        position,
      } as any);
      setShowAddForm(false);
      setAddQuery('');
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to add', { variant: 'error' });
    }
  };

  const typeColors: Record<string, string> = {
    Workflow: 'bg-purple-900/40 text-purple-300',
    Task: 'bg-blue-900/40 text-blue-300',
    Outcome: 'bg-green-900/40 text-green-300',
  };

  return (
    <div>
      {items.length === 0 && !showAddForm ? (
        <div className="text-sm text-cm-text-muted text-center py-4 border border-dashed border-cm-border-mid rounded-lg">
          No items prioritized yet.
          {editable && (
            <button
              onClick={() => setShowAddForm(true)}
              className="text-cm-accent-orange hover:underline ml-1"
            >
              Add an item
            </button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-cm-border-subtle">
          {items.map((item: any, idx: number) => {
            const { label, type, link } = labelForEntity(item.entity_id);
            return (
              <div key={item.id || idx} className="flex items-center justify-between py-2.5 gap-3 group">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-xs font-mono text-cm-text-muted w-4 text-right shrink-0">
                    {idx + 1}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${typeColors[type] || 'bg-gray-800 text-gray-300'}`}>
                    {type}
                  </span>
                  {link ? (
                    <Link
                      to={link}
                      className="text-sm text-cm-text-primary hover:text-cm-accent-orange transition-colors truncate"
                    >
                      {label}
                    </Link>
                  ) : (
                    <span className="text-sm text-cm-text-primary truncate">{label}</span>
                  )}
                </div>
                {editable && (
                  <button
                    onClick={() => handleRemove(item.id)}
                    className="text-cm-text-muted hover:text-red-400 p-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove from priorities"
                  >
                    <FiX className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add item form */}
      {editable && items.length > 0 && !showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="mt-3 flex items-center gap-1.5 text-xs text-cm-text-muted hover:text-cm-accent-orange transition-colors"
        >
          <FiPlus className="w-3.5 h-3.5" />
          Add item
        </button>
      )}

      {showAddForm && (
        <div className="mt-3 border border-cm-accent-orange/30 rounded-lg p-3">
          <div className="relative mb-2">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cm-text-muted" />
            <input
              type="text"
              value={addQuery}
              onChange={(e) => setAddQuery(e.target.value)}
              placeholder="Search tasks, outcomes, workflows..."
              autoFocus
              className="w-full pl-9 pr-3 py-1.5 bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:border-cm-accent-orange focus:ring-cm-accent-orange"
            />
          </div>
          {filteredAddable.length > 0 ? (
            <div className="max-h-40 overflow-y-auto divide-y divide-cm-border-subtle">
              {filteredAddable.map((e) => (
                <button
                  key={`${e.type_}-${e.id}`}
                  onClick={() => handleAdd(e)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-cm-text-primary hover:bg-cm-bg-hover transition-colors text-left"
                >
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                    typeColors[e.type_ === 'WorkflowInstance' ? 'Workflow' : e.type_] || 'bg-gray-800 text-gray-300'
                  }`}>
                    {e.type_ === 'WorkflowInstance' ? 'Workflow' : e.type_}
                  </span>
                  <span className="truncate">{e.label}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-cm-text-muted text-center py-2">
              {addQuery ? `No results for "${addQuery}"` : 'No items available to add'}
            </p>
          )}
          <button
            onClick={() => { setShowAddForm(false); setAddQuery(''); }}
            className="mt-2 text-xs text-cm-text-muted hover:text-cm-text-primary transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default PriorityItemsList;
